import { useMemo } from "react";
import {
  useListStateInfoQuery,
  useListTaxRatesQuery,
} from "features/trading/accounts";

/**
 * State tax rate (percent) used by the strategy report, plus the rows the
 * state picker needs.
 *
 * A stored per-client setting rather than a browser-local one — see
 * useFederalTaxRate for the federal rate. Takes the client id instead of
 * reading the report settings context so it stays usable from anywhere.
 */
export const useStateTaxRate = (clientId: number | null) => {
  const { data: taxRatesData } = useListTaxRatesQuery(
    {
      pagination: { page: 0, pageSize: 100 },
      sorting: [{ field: "id", sort: "desc" }],
      filters: clientId ? { client: clientId } : {},
    },
    { skip: !clientId },
  );
  const { data: statesData, isLoading: isLoadingStates } =
    useListStateInfoQuery({
      pagination: { page: 0, pageSize: 100 },
      sorting: [{ field: "state_name", sort: "asc" }],
    });

  const clientTaxRate = useMemo(
    () =>
      taxRatesData?.results?.find((taxRate) => taxRate.client === clientId) ??
      null,
    [taxRatesData, clientId],
  );

  // The state the client is mapped to carries the rate; the mapping itself
  // only holds the state id.
  const stateTaxRate = useMemo(() => {
    if (!clientTaxRate) return null;
    const state = statesData?.results?.find(
      (stateInfo) => stateInfo.id === clientTaxRate.state,
    );
    // DecimalField over the wire, so it arrives as a string.
    const parsedRate = Number(state?.tax_rate);
    if (state?.tax_rate == null || !Number.isFinite(parsedRate)) return null;
    return parsedRate;
  }, [clientTaxRate, statesData]);

  return {
    stateTaxRate,
    clientTaxRate,
    states: statesData?.results ?? [],
    isLoadingStates,
  };
};
